/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Main Entry Point - React application initialization and setup
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

// Sentry must be initialized before other imports
import * as Sentry from '@sentry/react'

import { createLogger } from './utils/logger'

const logger = createLogger('main')

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    // Performance monitoring
    tracesSampleRate: 1.0,
    // Session replay for debugging
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    // Environment
    environment: import.meta.env.MODE
  })
  logger.debug('✅ Sentry initialized (frontend)')
} else {
  logger.debug('⚠️  Sentry disabled (VITE_SENTRY_DSN not set)')
}

// Initialize browser tracing (Application Insights OR SigNoz, not both)
// Application Insights takes precedence if both are configured
// Both are conditionally enabled via their respective environment variables
const hasAppInsights = !!import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING
const hasSignoz = !!import.meta.env.VITE_SIGNOZ_ENDPOINT

if (hasAppInsights) {
  import('./services/appInsightsTracing').then((m) => m.default.init())
  if (hasSignoz) {
    logger.debug(
      '⚠️  Warning: VITE_SIGNOZ_ENDPOINT is set but will be ignored (Application Insights takes precedence)'
    )
  }
} else if (hasSignoz) {
  import('./services/signozTracing').then((m) => m.default.init())
}

// Initialize OpenReplay session replay (can run alongside other analytics)
const hasOpenReplay = !!import.meta.env.VITE_OPENREPLAY_PROJECT_KEY
if (hasOpenReplay) {
  import('./services/openReplayService').then((m) => {
    m.default.init()
    m.default.start()
  })
}

import React from 'react'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Toaster } from 'react-hot-toast'
import App from './App.jsx'
import useThemeStore from './stores/themeStore'
import ErrorBoundary from './components/ErrorBoundary'
import loggingService from './services/loggingService'
import './index.css'

// Ensure fetch interceptor is set up
loggingService.initialize?.()

// Navigation logging utility
const logNavigation = (location, action) => {
  // Disabled for now
  // const timestamp = new Date().toISOString()
  // const logData = {
  //   timestamp,
  //   action,
  //   pathname: location.pathname,
  //   search: location.search,
  //   hash: location.hash,
  //   fullPath: location.pathname + location.search + location.hash,
  //   userAgent: navigator.userAgent,
  //   referrer: document.referrer
  // }
  // console.group(`🧭 Navigation ${action}`)
  // console.log('📍 Path:', logData.fullPath)
  // console.log('⏰ Time:', logData.timestamp)
  // console.log('🔍 Search params:', logData.search)
  // console.log('🔗 Hash:', logData.hash)
  // console.log('📊 Full data:', logData)
  // console.groupEnd()
  // // Also log to localStorage for persistence across sessions
  // try {
  //   const navigationHistory = JSON.parse(localStorage.getItem('navigationHistory') || '[]')
  //   navigationHistory.push(logData)
  //   // Keep only last 50 navigation events
  //   if (navigationHistory.length > 50) {
  //     navigationHistory.splice(0, navigationHistory.length - 50)
  //   }
  //   localStorage.setItem('navigationHistory', JSON.stringify(navigationHistory))
  //   // Dispatch custom event to notify debugger (instead of polling)
  //   window.dispatchEvent(new CustomEvent('navigationLogged', { detail: logData }))
  // } catch (error) {
  //   console.warn('Failed to save navigation history:', error)
  // }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
})

ReactDOM.createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <NavigationLogger />
          <App />
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff'
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#fff'
                }
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff'
                }
              }
            }}
          />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
)

// Hide the initial loader once React has mounted
const initialLoader = document.getElementById('initial-loader')
if (initialLoader) {
  // Signal successful mount to clear the stuck-loading timer
  window.__appMounted = true
  if (window.__clearLoadingTimer) window.__clearLoadingTimer()

  initialLoader.style.opacity = '0'
  setTimeout(() => initialLoader.remove(), 200)
}

// Navigation Logger Component
function NavigationLogger() {
  const location = window.location

  React.useEffect(() => {
    // Log initial page load
    //logNavigation(location, 'INITIAL_LOAD')
    // loggingService.logNavigation('', location.pathname, {
    //   action: 'INITIAL_LOAD',
    //   search: location.search,
    //   hash: location.hash
    // })

    // Log browser back/forward navigation
    const handlePopState = () => {
      logNavigation(window.location, 'POP_STATE')
      loggingService.logNavigation(location.pathname, window.location.pathname, {
        action: 'POP_STATE',
        search: window.location.search,
        hash: window.location.hash
      })
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  return null // This component doesn't render anything
}
